import type { ReactNode, RefObject } from "react";
import MapGL, { Marker, Popup, NavigationControl } from "react-map-gl/mapbox";
import type { MapRef } from "react-map-gl/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";

// Single react-map-gl entry point: primitives and hooks import the pieces
// they need from here, keeping the library boundary in one file.
export { Marker, Popup, NavigationControl };
export type { MapRef };

export interface MapViewState {
  longitude: number;
  latitude: number;
  zoom: number;
  pitch: number;
}

// Static CSS hoisted out of the render path — the canvas re-renders on every
// camera frame and must not rebuild this string.
const MAP_CANVAS_CSS = `
  .hide-mapbox-logo .mapboxgl-ctrl-logo {
    display: none !important;
  }
  .poi-detail-popup.mapboxgl-popup .mapboxgl-popup-content {
    padding: 14px 36px 14px 14px;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.12);
    min-width: 200px;
    max-width: 280px;
  }
  .poi-detail-popup.mapboxgl-popup .mapboxgl-popup-close-button {
    font-size: 20px;
    padding: 4px 8px;
    right: 2px;
    top: 2px;
  }
  .mapboxgl-map:has(.poi-detail-popup) .mapboxgl-popup-backdrop,
  .mapboxgl-map:has(.poi-detail-popup) .mapboxgl-popup-overlay,
  .mapboxgl-map:has(.poi-detail-popup)::before,
  .poi-detail-popup.mapboxgl-popup::before,
  .poi-detail-popup.mapboxgl-popup::after {
    display: none !important;
    background: transparent !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }
`;

interface MapCanvasProps {
  mapRef: RefObject<MapRef>;
  viewState: MapViewState;
  onMove: (evt: { viewState: MapViewState }) => void;
  onClick?: () => void;
  /** Mapbox attribution control (canvas chrome, on by default). */
  attribution?: boolean;
  /** Mapbox logo (canvas chrome, on by default). */
  logo?: boolean;
  /** When `false`, disables scroll-zoom/drag-pan/rotate so the map doesn't
   * hijack page scroll or drag gestures meant for a surrounding scrollable
   * container (e.g. a drawer, before the user has clicked to activate the
   * map). Defaults to `true`. */
  interactive?: boolean;
  children?: ReactNode;
}

export function MapCanvas({
  mapRef,
  viewState,
  onMove,
  onClick,
  attribution = true,
  logo = true,
  interactive = true,
  children,
}: MapCanvasProps) {
  return (
    <div
      className={logo ? undefined : "hide-mapbox-logo"}
      style={{ width: "100%", height: "100%" }}
    >
      <MapGL
        ref={mapRef}
        {...viewState}
        onMove={onMove}
        onClick={onClick}
        mapboxAccessToken={import.meta.env.VITE_MAPBOX_ACCESS_TOKEN}
        style={{ width: "100%", height: "100%" }}
        mapStyle="mapbox://styles/mapbox/light-v11"
        attributionControl={attribution}
        scrollZoom={interactive}
        dragPan={interactive}
        dragRotate={interactive}
        doubleClickZoom={interactive}
        touchZoomRotate={interactive}
      >
        <style>{MAP_CANVAS_CSS}</style>
        {children}
      </MapGL>
    </div>
  );
}
